/**
 * Decides how many leading nav entries fit inside `containerWidth`, given
 * each entry's own measured width, the width the "More" trigger would need
 * if anything has to collapse into it, and the flex `gap` that sits between
 * every rendered entry (`.main-nav` uses `gap: 0.25rem` — a row of N
 * elements carries N-1 of these, and that add-on is exactly as real as the
 * entries themselves, so it's a parameter here rather than folded into the
 * caller's measured widths).
 *
 * Pure and DOM-free on purpose (T-082): `MainNav` is the thing that measures
 * real elements and hands the numbers in here — this function never touches
 * layout, so the decision is testable without a real browser. jsdom/happy-dom
 * always report 0 for `offsetWidth`, so this split is the only way to test
 * the collapsing logic at all.
 *
 * Returns the count of leading entries that fit unchanged; everything from
 * that index onward collapses into "More", in order. `moreWidth` (and the gap
 * before it) is only reserved once it's actually needed — if every entry
 * already fits on its own, nothing is set aside for the trigger.
 */
export function fitNavItems(containerWidth: number, itemWidths: readonly number[], moreWidth: number, gap: number): number {
  const totalCount = itemWidths.length;
  const totalWidth = itemWidths.reduce((sum, width) => sum + width, 0) + gap * Math.max(totalCount - 1, 0);
  if (totalWidth <= containerWidth) return totalCount;

  let used = 0;
  let fitCount = 0;
  for (const width of itemWidths) {
    // fitCount + 1 elements would be visible (this item plus every one
    // before it) alongside More, so there are fitCount + 1 gaps: one
    // between each pair of visible items, and one before More.
    const candidateWidth = used + width + gap * (fitCount + 1) + moreWidth;
    if (candidateWidth > containerWidth) break;
    used += width;
    fitCount++;
  }
  return fitCount;
}
